import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { RuleType } from "../types/pdf-generator";

export const DocumentPreview = ({ config, rules, pageFormat, standard }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);

  const findRule = (ruleId) => {
    return rules?.rules?.find((rule) => rule.id === ruleId);
  };

  // Fonction pour diviser le contenu en pages avec flux naturel de lecture
  const splitContentIntoPages = (rules, pageFormat) => {
    if (!rules || !rules.length) return [];

    const [pageWidth, pageHeight] = pageFormat;

    const headerHeight = (config.mainTitleFontSize || 18) * 3 + 10; // Hauteur de l'en-tête
    const availableHeight =
      pageHeight - (config.topMargin || 0) - (config.bottomMargin || 0);

    const pages = [];
    let currentPageRules = [];
    let currentHeight = 0;

    const characterWidth = (config.ruleFontSize || 11) * 0.5;
    const lineHeight =
      (config.ruleFontSize || 11) * (config.lineSpacing || 1.2);
    const maxWidth =
      pageWidth -
      (config.leftMargin || 0) -
      (config.rightMargin || 0) -
      (config.shiftSize || 0) -
      (config.columnCount - 1) * 8;

    const maxWidthExample = maxWidth - 2 * (config.examples?.marginX || 0);

    const titleHeight =
      (config.ruleTitleFontSize || 13) * (config.lineSpacing || 1.2);

    const headingHeight =
      (config.headerFontSize || 13) * (config.lineSpacing || 1.2);

    // Pour la première page, on doit tenir compte de l'en-tête
    let contentHeight = availableHeight - headerHeight;

    let ruleEstimatedHeight = 0;
    let pushWithNextRule = null;

    // Traiter toutes les règles dans l'ordre
    rules.forEach((rule, ruleIndex) => {
      if (ruleIndex > 50) return; // Limiter pour l'aperçu

      ruleEstimatedHeight = 0;
      switch (rule.type) {
        case RuleType.HEADING:
          pushWithNextRule = rule;
          return;

        case RuleType.EXAMPLE:
        case RuleType.PROCEDURE:
        case RuleType.RULE:
          const lenText = rule.text.length;
          // compter le nommbre de <br/> et <li>
          const minimumLine =
            (rule.text.match(/<br\s*\/?>/g)?.length || 0) +
            (rule.text.match(/<li\s*\/?>/g)?.length || 0);

          const width =
            rule.type === RuleType.EXAMPLE ? maxWidthExample : maxWidth;

          ruleEstimatedHeight =
            ((lenText * characterWidth) / width + minimumLine / 2) * lineHeight;
          ruleEstimatedHeight += titleHeight;
          break;
      }
      ruleEstimatedHeight = Math.round(ruleEstimatedHeight);
      // console.log(ruleEstimatedHeight, rule.text);

      // Si on dépasse la hauteur de la page, créer une nouvelle page
      if (
        currentHeight + ruleEstimatedHeight > contentHeight &&
        currentPageRules.length > 0
      ) {
        pages.push([...currentPageRules]);
        currentPageRules = [];
        currentHeight = 0;
        // Pour les pages suivantes, pas d'en-tête donc plus d'espace
        contentHeight = availableHeight;
      }

      // Si c'est un chapitre, jamais en fin de page donc on ajoute le chapitre à la page courante
      if (pushWithNextRule) {
        currentPageRules.push(pushWithNextRule);
        currentHeight += headingHeight;
        pushWithNextRule = null;
      }

      currentPageRules.push(rule);
      currentHeight += ruleEstimatedHeight;
    });

    // Ajouter la dernière page si elle contient du contenu
    if (currentPageRules.length > 0) {
      pages.push(currentPageRules);
    }

    return pages.length > 0 ? pages : [[]];
  };

  // Diviser le contenu en pages
  const columnCount = config.columnCount || 1;
  const pageHeight = pageFormat[1]; // Hauteur réduite pour l'aperçu
  const marginBottom = config.marginBottom || 0;

  const currentRules = rules?.rules.filter(
    (rule) =>
      rule.type === RuleType.HEADING ||
      rule.type === RuleType.RULE ||
      (rule.type === RuleType.PROCEDURE &&
        config.includeProcedures !== false) ||
      (rule.type === RuleType.EXAMPLE && config.includeExamples !== false)
  );
  const pages = splitContentIntoPages(currentRules || [], pageFormat);

  // Fonction pour diviser les règles d'une page en colonnes
  const splitPageIntoColumns = (pageRules, columnCount) => {
    if (!pageRules || columnCount <= 1) {
      return [pageRules];
    }

    const columns = Array.from({ length: columnCount }, () => []);
    const rulesPerColumn = Math.ceil(pageRules.length / columnCount);

    pageRules.forEach((rule, index) => {
      const columnIndex = Math.floor(index / rulesPerColumn);
      if (columns[columnIndex]) {
        columns[columnIndex].push(rule);
      }
    });

    return columns;
  };

  const renderPage = (pageRules, pageIndex) => {
    // Diviser les règles de cette page en colonnes
    const pageColumns = splitPageIntoColumns(pageRules, columnCount);

    const hasHeader = pageIndex === 0; // En-tête seulement sur la première page
    const headerHeight = hasHeader
      ? (config.mainTitleFontSize || 18) * 3 + 70
      : config.topMargin;

    return (
      <div
        key={pageIndex}
        className="relative mb-4 bg-gray-50 rounded-sm border shadow-sm text-gray-950 border-primary-300"
        style={{
          width: pageFormat[0],
          height: pageHeight + config.bottomMargin,
          maxWidth: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        }}
      >
        {/* En-tête de la page - UNIQUEMENT SUR LA PREMIÈRE PAGE ET FOND BLANC */}
        {hasHeader && (
          <DocumentHeader
            config={config}
            meta={rules.meta}
            fontFamily={config.fontFamily || standard.fontFamily}
            title={findRule("TOP")?.title}
          />
        )}

        {/* Contenu de la page en colonnes - FLUX NATUREL DE LECTURE */}
        <div
          className="flex gap-4"
          style={{
            height: pageHeight - 2 * marginBottom - headerHeight,
            overflow: "hidden",
            marginLeft: config.leftMargin || "0",
            marginRight: config.rightMargin || "0",
            marginTop: hasHeader ? "0" : config.topMargin || "0",
            // padding: "10px",
          }}
        >
          {pageColumns.map((columnRules, columnIndex) => (
            <div
              key={columnIndex}
              className="flex-1"
              style={{
                width: `${100 / columnCount}%`,
                paddingRight: columnIndex < columnCount - 1 ? "8px" : "0",
              }}
            >
              {columnRules.map((rule, index) => {
                if (rule.type === RuleType.HEADING) {
                  return (
                    <Fragment key={rule.id}>
                      <div
                        style={{
                          fontSize: `${config.headerFontSize || 13}px`,
                          fontWeight: "600",
                          textAlign: "center",
                          fontFamily: config.fontFamily || standard.fontFamily,
                          lineHeight: config.lineSpacing || 1.2,
                          paddingTop: 10,
                          paddingBottom: 5,
                        }}
                      >
                        {rule.title.toUpperCase()}
                      </div>
                      <div
                        style={{
                          fontSize: `${config.ruleFontSize || 11}px`,
                          fontFamily: config.fontFamily || standard.fontFamily,
                          textAlign: config.textAlign || "left",
                          lineHeight: config.lineSpacing || 1.2,
                        }}
                        dangerouslySetInnerHTML={{ __html: rule?.text || "" }}
                      />
                    </Fragment>
                  );
                }
                if (
                  rule.type === RuleType.RULE ||
                  rule.type === RuleType.PROCEDURE
                ) {
                  return (
                    <Fragment key={rule.id}>
                      <div
                        style={{
                          fontSize: `${config.ruleTitleFontSize || 13}px`,
                          fontWeight: "600",
                          fontFamily: config.fontFamily || standard.fontFamily,
                          lineHeight: config.lineSpacing || 1.2,
                          paddingTop: 5,
                          paddingBottom: 2,
                        }}
                      >
                        {rule.id + " - " + rule.title}
                      </div>
                      <div
                        style={{
                          fontSize: `${config.ruleFontSize || 11}px`,
                          fontFamily: config.fontFamily || standard.fontFamily,
                          textAlign: config.textAlign || "left",
                          lineHeight: config.lineSpacing || 1.2,
                        }}
                        dangerouslySetInnerHTML={{ __html: rule?.text || "" }}
                      />
                    </Fragment>
                  );
                } else if (
                  rule.type === RuleType.EXAMPLE &&
                  config.includeExamples !== false &&
                  config.examples?.inline !== false
                ) {
                  return (
                    <div
                      key={rule.id}
                      style={{
                        fontFamily: config.fontFamily || standard.fontFamily,
                        lineHeight: config.lineSpacing || 1.2,
                        padding: "8px 10px",
                        marginTop: config.examples?.marginY || "0",
                        marginLeft: config.examples?.marginX || "0",
                        marginRight: config.examples?.marginX || "0",
                        backgroundColor: config.examples?.bgColor || "#f0f0f0",
                        borderRadius: config.examples?.radius || "4px",
                        borderColor: config.examples?.borderColor || "none",
                        borderWidth: config.examples?.borderWidth || "1px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: `${config.ruleTitleFontSize || 13}px`,
                          fontWeight: "600",
                          lineHeight: config.lineSpacing || 1.2,
                        }}
                      >
                        {rule.title}
                      </div>
                      <div
                        style={{
                          fontSize: `${config.examples?.fontSize || 11}px`,
                          fontWeight: "100",
                          fontFamily: config.fontFamily || standard.fontFamily,
                          textAlign: config.textAlign || "left",
                          lineHeight:
                            config.examples?.lineSpacing ||
                            config.lineSpacing ||
                            1.2,
                        }}
                        dangerouslySetInnerHTML={{ __html: rule?.text || "" }}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          ))}
        </div>

        {/* Numéro de page */}
        <div
          className="text-xs text-center text-gray-500"
          style={{
            position: "absolute",
            top: config.topMargin / 2 - 10,
            right: config.rightMargin / 2,
          }}
        >
          {pageIndex + 1}
        </div>
        {/* header */}
        <div
          className="text-xs text-center text-gray-500"
          style={{
            position: "absolute",
            top: config.topMargin / 2 - 10,
            alignSelf: "center",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {rules?.meta?.pdf_title}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 mt-4 rounded-lg border border-primary-300 bg-background-100">
      <div className="mb-3 text-sm font-medium text-text-700">
        {t("preview.typography.title")}
      </div>

      {/* Navigation des pages */}
      {pages.length > 1 && (
        <div className="flex gap-4 justify-center items-center mb-4">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="px-3 py-1 text-sm rounded border color-text bg-secondary-300 border-secondary-200 disabled:opacity-50 hover:bg-secondary-400 hover:border-secondary-800"
          >
            {t("preview.navigation.previous")}
          </button>
          <span className="text-sm">
            {t("preview.navigation.page")} {currentPage + 1} / {pages.length}
          </span>
          <button
            onClick={() =>
              setCurrentPage(Math.min(pages.length - 1, currentPage + 1))
            }
            disabled={currentPage === pages.length - 1}
            className="px-3 py-1 text-sm rounded border color-text bg-secondary-300 border-secondary-200 disabled:opacity-50 hover:bg-secondary-400 hover:border-secondary-800"
          >
            {t("preview.navigation.next")}
          </button>
        </div>
      )}

      {/* Affichage de la page courante */}
      <div className="flex justify-center">
        {renderPage(pages[currentPage] || [], currentPage)}
      </div>
    </div>
  );
};

const DocumentHeader = ({ config, meta, fontFamily, title }) => {
  return (
    <div
      className="space-y-2"
      style={{
        color: "#111111",
        lineHeight: config.lineSpacing || 1.2,
        marginLeft: config.leftMargin || "0",
        marginRight: config.rightMargin || "0",
        marginTop: config.topMargin || "0",
        marginBottom: 10,
      }}
    >
      <div className="flex flex-row gap-2 items-center">
        <img
          src={meta?.logo_path}
          width={config.logoWidth || 100}
          height={config.logoWidth * config.logoRatio}
        />
        <div className="flex flex-col items-center w-full">
          <div
            style={{
              fontSize: `${config.mainTitleFontSize || 18}px`,
              fontWeight: "600",
              fontFamily: fontFamily,
              lineHeight: config.lineSpacing || 1.2,
            }}
          >
            {config.mainTitle}
          </div>
          <div
            style={{
              fontSize: `${config.mainTitleFontSize * 0.8 || 18}px`,
              fontWeight: "bold",
              fontFamily: fontFamily,
              lineHeight: config.lineSpacing / 0.8,
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: `${config.mainTitleFontSize * 0.7 || 18}px`,
              fontWeight: "bold",
              fontFamily: fontFamily,
              color: "#666666",
              lineHeight: config.lineSpacing / 0.7,
            }}
          >
            {meta?.author || t("preview.typography.author")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
